import { __decorate, __extends } from "tslib";
import { serialize } from "../Misc/decorators";
import { ImageProcessingConfiguration } from "../Materials/imageProcessingConfiguration";
import { PostProcess } from "./postProcess";
import { EngineStore } from "../Engines/engineStore";
import "../Shaders/imageProcessing.fragment";
import "../Shaders/postprocess.vertex";
/**
 * ImageProcessingPostProcess
 * @see https://doc.babylonjs.com/how_to/how_to_use_postprocesses#imageprocessing
 */
var ImageProcessingPostProcess = /** @class */ (function (_super) {
    __extends(ImageProcessingPostProcess, _super);
    function ImageProcessingPostProcess(name, options, camera, samplingMode, engine, reusable, textureType, imageProcessingConfiguration) {
        if (camera === void 0) { camera = null; }
        if (textureType === void 0) { textureType = 0; }
        var _this = _super.call(this, name, "imageProcessing", [], [], options, camera, samplingMode, engine, reusable, null, textureType, "postprocess", null, true) || this;
        _this._fromLinearSpace = true;
        /**
         * Defines cache preventing GC.
         */
        _this._defines = {
            IMAGEPROCESSING: false,
            VIGNETTE: false,
            VIGNETTEBLENDMODEMULTIPLY: false,
            VIGNETTEBLENDMODEOPAQUE: false,
            TONEMAPPING: false,
            TONEMAPPING_ACES: false,
            CONTRAST: false,
            COLORCURVES: false,
            COLORGRADING: false,
            COLORGRADING3D: false,
            FROMLINEARSPACE: false,
            SAMPLER3DGREENDEPTH: false,
            SAMPLER3DBGRMAP: false,
            IMAGEPROCESSINGPOSTPROCESS: false,
            EXPOSURE: false,
        };
        // Setup the configuration as forced by the constructor. This would then not force the
        // scene materials output in linear space and let untouched the default forward pass.
        if (imageProcessingConfiguration) {
            imageProcessingConfiguration.applyByPostProcess = true;
            _this._attachImageProcessingConfiguration(imageProcessingConfiguration, true);
            // This will cause the shader to be compiled
            _this.fromLinearSpace = false;
        }
        // Setup the default processing configuration to the scene.
        else {
            _this._attachImageProcessingConfiguration(null, true);
            _this.imageProcessingConfiguration.applyByPostProcess = true;
        }
        _this.onApply = function (effect) {
            _this.imageProcessingConfiguration.bind(effect, _this.aspectRatio);
        };
        return _this;
    }
    Object.defineProperty(ImageProcessingPostProcess.prototype, "imageProcessingConfiguration", {
        /**
         * Gets the image processing configuration used either in this material.
         */
        get: function () {
            return this._imageProcessingConfiguration;
        },
        /**
         * Sets the Default image processing configuration used either in the this material.
         *
         * If sets to null, the scene one is in use.
         */
        set: function (value) {
            // We are almost sure it is applied by post process as
            // We are in the post process :-)
            value.applyByPostProcess = true;
            this._attachImageProcessingConfiguration(value);
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Attaches a new image processing configuration to the PBR Material.
     * @param configuration
     */
    ImageProcessingPostProcess.prototype._attachImageProcessingConfiguration = function (configuration, doNotBuild) {
        var _this = this;
        if (doNotBuild === void 0) { doNotBuild = false; }
        if (configuration === this._imageProcessingConfiguration) {
            return;
        }
        // Detaches observer.
        if (this._imageProcessingConfiguration && this._imageProcessingObserver) {
            this._imageProcessingConfiguration.onUpdateParameters.remove(this._imageProcessingObserver);
        }
        // Pick the scene configuration if needed.
        if (!configuration) {
            var scene = null;
            var engine = this.getEngine();
            var camera = this.getCamera();
            if (camera) {
                scene = camera.getScene();
            }
            else if (engine && engine.scenes) {
                var scenes = engine.scenes;
                scene = scenes[scenes.length - 1];
            }
            else {
                scene = EngineStore.LastCreatedScene;
            }
            if (scene) {
                this._imageProcessingConfiguration = scene.imageProcessingConfiguration;
            }
            else {
                this._imageProcessingConfiguration = new ImageProcessingConfiguration();
            }
        }
        else {
            this._imageProcessingConfiguration = configuration;
        }
        // Attaches observer.
        if (this._imageProcessingConfiguration) {
            this._imageProcessingObserver = this._imageProcessingConfiguration.onUpdateParameters.add(function () {
                _this._updateParameters();
            });
        }
        // Ensure the effect will be rebuilt.
        if (!doNotBuild) {
            this._updateParameters();
        }
    };
    Object.defineProperty(ImageProcessingPostProcess.prototype, "isSupported", {
        /**
         * If the post process is supported.
         */
        get: function () {
            var effect = this.getEffect();
            return !effect || effect.isSupported;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ImageProcessingPostProcess.prototype, "colorCurves", {
        /**
         * Gets Color curves setup used in the effect if colorCurvesEnabled is set to true .
         */
        get: function () {
            return this.imageProcessingConfiguration.colorCurves;
        },
        /**
         * Sets Color curves setup used in the effect if colorCurvesEnabled is set to true .
         */
        set: function (value) {
            this.imageProcessingConfiguration.colorCurves = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ImageProcessingPostProcess.prototype, "colorCurvesEnabled", {
        /**
         * Gets wether the color curves effect is enabled.
         */
        get: function () {
            return this.imageProcessingConfiguration.colorCurvesEnabled;
        },
        /**
         * Sets wether the color curves effect is enabled.
         */
        set: function (value) {
            this.imageProcessingConfiguration.colorCurvesEnabled = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ImageProcessingPostProcess.prototype, "colorGradingTexture", {
        /**
         * Gets Color grading LUT texture used in the effect if colorGradingEnabled is set to true.
         */
        get: function () {
            return this.imageProcessingConfiguration.colorGradingTexture;
        },
        /**
         * Sets Color grading LUT texture used in the effect if colorGradingEnabled is set to true.
         */
        set: function (value) {
            this.imageProcessingConfiguration.colorGradingTexture = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ImageProcessingPostProcess.prototype, "colorGradingEnabled", {
        /**
         * Gets wether the color grading effect is enabled.
         */
        get: function () {
            return this.imageProcessingConfiguration.colorGradingEnabled;
        },
        /**
         * Gets wether the color grading effect is enabled.
         */
        set: function (value) {
            this.imageProcessingConfiguration.colorGradingEnabled = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ImageProcessingPostProcess.prototype, "exposure", {
        /**
         * Gets exposure used in the effect.
         */
        get: function () {
            return this.imageProcessingConfiguration.exposure;
        },
        /**
         * Sets exposure used in the effect.
         */
        set: function (value) {
            this.imageProcessingConfiguration.exposure = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ImageProcessingPostProcess.prototype, "toneMappingEnabled", {
        /**
         * Gets wether tonemapping is enabled or not.
         */
        get: function () {
            return this._imageProcessingConfiguration.toneMappingEnabled;
        },
        /**
         * Sets wether tonemapping is enabled or not
         */
        set: function (value) {
            this._imageProcessingConfiguration.toneMappingEnabled = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ImageProcessingPostProcess.prototype, "toneMappingType", {
        /**
         * Gets the type of tone mapping effect.
         */
        get: function () {
            return this._imageProcessingConfiguration.toneMappingType;
        },
        /**
         * Sets the type of tone mapping effect.
         */
        set: function (value) {
            this._imageProcessingConfiguration.toneMappingType = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ImageProcessingPostProcess.prototype, "contrast", {
        /**
         * Gets contrast used in the effect.
         */
        get: function () {
            return this.imageProcessingConfiguration.contrast;
        },
        /**
         * Sets contrast used in the effect.
         */
        set: function (value) {
            this.imageProcessingConfiguration.contrast = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ImageProcessingPostProcess.prototype, "vignetteStretch", {
        /**
         * Gets Vignette stretch size.
         */
        get: function () {
            return this.imageProcessingConfiguration.vignetteStretch;
        },
        /**
         * Sets Vignette stretch size.
         */
        set: function (value) {
            this.imageProcessingConfiguration.vignetteStretch = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ImageProcessingPostProcess.prototype, "vignetteCentreX", {
        /**
         * Gets Vignette centre X Offset.
         */
        get: function () {
            return this.imageProcessingConfiguration.vignetteCentreX;
        },
        /**
         * Sets Vignette centre X Offset.
         */
        set: function (value) {
            this.imageProcessingConfiguration.vignetteCentreX = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ImageProcessingPostProcess.prototype, "vignetteCentreY", {
        /**
         * Gets Vignette centre Y Offset.
         */
        get: function () {
            return this.imageProcessingConfiguration.vignetteCentreY;
        },
        /**
         * Sets Vignette centre Y Offset.
         */
        set: function (value) {
            this.imageProcessingConfiguration.vignetteCentreY = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ImageProcessingPostProcess.prototype, "vignetteWeight", {
        /**
         * Gets Vignette weight or intensity of the vignette effect.
         */
        get: function () {
            return this.imageProcessingConfiguration.vignetteWeight;
        },
        /**
         * Sets Vignette weight or intensity of the vignette effect.
         */
        set: function (value) {
            this.imageProcessingConfiguration.vignetteWeight = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ImageProcessingPostProcess.prototype, "vignetteColor", {
        /**
         * Gets Color of the vignette applied on the screen through the chosen blend mode (vignetteBlendMode)
         * if vignetteEnabled is set to true.
         */
        get: function () {
            return this.imageProcessingConfiguration.vignetteColor;
        },
        /**
         * Sets Color of the vignette applied on the screen through the chosen blend mode (vignetteBlendMode)
         * if vignetteEnabled is set to true.
         */
        set: function (value) {
            this.imageProcessingConfiguration.vignetteColor = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ImageProcessingPostProcess.prototype, "vignetteCameraFov", {
        /**
         * Gets Camera field of view used by the Vignette effect.
         */
        get: function () {
            return this.imageProcessingConfiguration.vignetteCameraFov;
        },
        /**
         * Sets Camera field of view used by the Vignette effect.
         */
        set: function (value) {
            this.imageProcessingConfiguration.vignetteCameraFov = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ImageProcessingPostProcess.prototype, "vignetteBlendMode", {
        /**
         * Gets the vignette blend mode allowing different kind of effect.
         */
        get: function () {
            return this.imageProcessingConfiguration.vignetteBlendMode;
        },
        /**
         * Sets the vignette blend mode allowing different kind of effect.
         */
        set: function (value) {
            this.imageProcessingConfiguration.vignetteBlendMode = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ImageProcessingPostProcess.prototype, "vignetteEnabled", {
        /**
         * Gets wether the vignette effect is enabled.
         */
        get: function () {
            return this.imageProcessingConfiguration.vignetteEnabled;
        },
        /**
         * Sets wether the vignette effect is enabled.
         */
        set: function (value) {
            this.imageProcessingConfiguration.vignetteEnabled = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ImageProcessingPostProcess.prototype, "fromLinearSpace", {
        /**
         * Gets wether the input of the processing is in Gamma or Linear Space.
         */
        get: function () {
            return this._fromLinearSpace;
        },
        /**
         * Sets wether the input of the processing is in Gamma or Linear Space.
         */
        set: function (value) {
            if (this._fromLinearSpace === value) {
                return;
            }
            this._fromLinearSpace = value;
            this._updateParameters();
        },
        enumerable: false,
        configurable: true
    });
    /**
     *  "ImageProcessingPostProcess"
     * @returns "ImageProcessingPostProcess"
     */
    ImageProcessingPostProcess.prototype.getClassName = function () {
        return "ImageProcessingPostProcess";
    };
    /**
     * @hidden
     */
    ImageProcessingPostProcess.prototype._updateParameters = function () {
        this._defines.FROMLINEARSPACE = this._fromLinearSpace;
        this.imageProcessingConfiguration.prepareDefines(this._defines, true);
        var defines = "";
        for (var define in this._defines) {
            if (this._defines[define]) {
                defines += "#define " + define + ";\r\n";
            }
        }
        var samplers = ["textureSampler"];
        var uniforms = ["scale"];
        if (ImageProcessingConfiguration) {
            ImageProcessingConfiguration.PrepareSamplers(samplers, this._defines);
            ImageProcessingConfiguration.PrepareUniforms(uniforms, this._defines);
        }
        this.updateEffect(defines, uniforms, samplers);
    };
    ImageProcessingPostProcess.prototype.dispose = function (camera) {
        _super.prototype.dispose.call(this, camera);
        if (this._imageProcessingConfiguration && this._imageProcessingObserver) {
            this._imageProcessingConfiguration.onUpdateParameters.remove(this._imageProcessingObserver);
        }
        if (this._imageProcessingConfiguration) {
            this.imageProcessingConfiguration.applyByPostProcess = false;
        }
    };
    __decorate([
        serialize()
    ], ImageProcessingPostProcess.prototype, "_fromLinearSpace", void 0);
    return ImageProcessingPostProcess;
}(PostProcess));
export { ImageProcessingPostProcess };
//# sourceMappingURL=imageProcessingPostProcess.js.map